export function colorKey(url) {
  if (!url) return null;
  const name = url.split(' ')[0].split('?')[0].split('/').pop() || '';
  const match = name.match(/^(.+?)_\d/);
  return match ? match[1].toUpperCase() : null;
}

export function orderByColor(media, leadId) {
  if (!Array.isArray(media) || media.length === 0) return [];
  const NONE = ' none';
  let leadSrc = media[0].src;
  if (leadId != null && leadId !== '') {
    const found = media.find((m) => String(m.id) === String(leadId));
    if (found) leadSrc = found.src;
  }
  const featured = colorKey(leadSrc) || NONE;
  const groups = new Map();
  const seen = [];
  for (const item of media) {
    const c = colorKey(item.src) || NONE;
    if (!groups.has(c)) {
      groups.set(c, []);
      seen.push(c);
    }
    groups.get(c).push(item);
  }
  const keys = groups.has(featured) ? [featured, ...seen.filter((k) => k !== featured)] : seen;
  return keys.flatMap((k) => groups.get(k));
}

export function pickWindow(media, max = 4, leadId) {
  return orderByColor(media, leadId).slice(0, max);
}

if (typeof customElements !== 'undefined' && !customElements.get('product-card-gallery')) {
  customElements.define(
    'product-card-gallery',
    class ProductCardGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        let media = [];
        try {
          media = JSON.parse(this.querySelector('[data-card-media]')?.textContent || '[]');
        } catch (e) {
          media = [];
        }
        this.items = pickWindow(media, 4, this.dataset.leadMediaId);
        if (this.items.length < 2) return;
        this.stage = this.querySelector('[data-card-stage]');
        if (!this.stage) return;
        this.index = 0;
        this.renderImages();
        this.renderDots();
        this.bindEvents();
        this.initialized = true;
      }

      renderImages() {
        this.stage.querySelectorAll('.card-gallery__img').forEach((el) => el.remove());
        const SIZES =
          '(max-width: 699px) 74vw, (max-width: 999px) 38vw, calc((100vw - 96px) / 3)';
        this.track = document.createElement('div');
        this.track.className = 'card-gallery__track';
        this.imgs = this.items.map((item, i) => {
          const img = document.createElement('img');
          img.className = 'card-gallery__img';
          img.src = item.src;
          if (item.srcset) {
            img.srcset = item.srcset;
            img.sizes = SIZES;
          }
          img.alt = item.alt || '';
          img.loading = i === 0 ? 'eager' : 'lazy';
          img.draggable = false;
          this.track.appendChild(img);
          return img;
        });
        this.stage.appendChild(this.track);
      }

      renderDots() {
        this.dotsWrap = document.createElement('div');
        this.dotsWrap.className = 'card-gallery__dots';
        this.dots = this.items.map((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'card-gallery__dot';
          b.setAttribute('aria-label', `Görsel ${i + 1}`);
          b.addEventListener('click', (e) => {
            e.preventDefault();
            this.goTo(i, false);
          });
          this.dotsWrap.appendChild(b);
          return b;
        });
        this.appendChild(this.dotsWrap);
        this.updateDots();
      }

      updateDots() {
        this.dots.forEach((d, n) => {
          const on = n === this.index;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-current', on ? 'true' : 'false');
        });
      }

      goTo(i, instant) {
        this.index = Math.min(Math.max(i, 0), this.items.length - 1);
        this.track.style.transition = instant ? 'none' : '';
        this.track.style.transform = `translateX(${-this.index * 100}%)`;
        this.updateDots();
      }

      withinStageY(clientY, rect) {
        return clientY >= rect.top && clientY <= rect.bottom;
      }

      bindEvents() {
        this.host = this.closest('.product-card') || this.stage;
        if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.host.addEventListener('mousemove', (e) => {
            const rect = this.stage.getBoundingClientRect();
            if (rect.width === 0 || !this.withinStageY(e.clientY, rect)) return;
            const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 0.9999);
            this.goTo(Math.floor(ratio * this.items.length), true);
          });
          this.host.addEventListener('mouseleave', () => this.goTo(0, true));
        }
        this.bindSwipe();
      }

      bindSwipe() {
        let startX = 0;
        let startY = 0;
        let basePx = 0;
        let lock = null;
        let active = false;
        const w = () => this.stage.clientWidth || 1;

        this.host.addEventListener(
          'touchstart',
          (e) => {
            const t = e.touches[0];
            const rect = this.stage.getBoundingClientRect();
            if (!this.withinStageY(t.clientY, rect)) {
              active = false;
              return;
            }
            active = true;
            lock = null;
            startX = t.clientX;
            startY = t.clientY;
            basePx = -this.index * w();
          },
          { passive: true }
        );

        this.host.addEventListener(
          'touchmove',
          (e) => {
            if (!active) return;
            const t = e.touches[0];
            const dx = t.clientX - startX;
            const dy = t.clientY - startY;
            if (lock === null) {
              if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
                lock = 'x';
              } else if (Math.abs(dy) > 8) {
                lock = 'y';
                active = false;
                return;
              } else {
                return;
              }
            }
            if (lock === 'x') {
              e.preventDefault();
              const px = Math.min(Math.max(basePx + dx, -(this.items.length - 1) * w()), 0);
              this.track.style.transition = 'none';
              this.track.style.transform = `translateX(${px}px)`;
            }
          },
          { passive: false }
        );

        this.host.addEventListener('touchend', (e) => {
          if (!active || lock !== 'x') {
            active = false;
            return;
          }
          active = false;
          const endX = e.changedTouches[0] ? e.changedTouches[0].clientX : startX;
          const dx = endX - startX;
          let i = this.index;
          if (Math.abs(dx) > w() * 0.18) i += dx < 0 ? 1 : -1;
          this.goTo(i, false);
        });
      }
    }
  );
}
